import { useCallback, useEffect, useSyncExternalStore } from 'react';
import api from '@/services/api';
import axios from 'axios';
import connectivityStore, { ConnectivityState } from '@/redux/slices/connectivitySlice';

/**
 * useConnectivity — real connectivity model.
 *
 * `navigator.onLine` alone lies in the failure mode that matters most at a
 * POS till: the Wi-Fi link is up but the store server is down, and the app
 * kept taking its ONLINE branch — sales were thrown away by a failing
 * createPosSale instead of being queued. This hook combines:
 *
 *   - navigator.onLine (window 'online'/'offline' events)
 *   - a lightweight GET /api/health probe (public route, no auth):
 *       · every 30s while mounted (idle interval)
 *       · immediately when the tab becomes visible again
 *       · expedited a few seconds after any observed failure
 *   - server observations fed in from the RTK base query (every API
 *     response marks 'up'; network errors / 5xx mark 'down')
 *
 * and exposes `isEffectivelyOnline` — the flag sale submission must branch on.
 *
 * The probe loop is module-level and reference-counted: SystemInfrastructure
 * Provider mounts it once app-wide, and re-mounts elsewhere never stack
 * intervals. Multiple probes are collapsed (single in-flight probe).
 */

const PROBE_INTERVAL_MS = 30_000;
const PROBE_TIMEOUT_MS = 8_000;
/** How soon after a failure we re-probe to catch quick recovery. */
const RETRY_PROBE_DELAY_MS = 5_000;

let probePromise: Promise<boolean> | null = null;
let probeUsers = 0;
let probeTimer: ReturnType<typeof setInterval> | null = null;
let retryProbeTimer: ReturnType<typeof setTimeout> | null = null;

function applyProbeResult(status: unknown): boolean {
  // A 4xx ANSWER still proves the server is reachable — only transport
  // failures and 5xx mean "server down".
  const reachable =
    typeof status === 'number' && status >= 400 && status < 500
      ? true
      : typeof status === 'number' && status >= 500
        ? false
        : status === undefined
          ? false
          : true;
  connectivityStore.markServerReachable(reachable ? 'up' : 'down');
  return reachable;
}

async function runHealthProbe(): Promise<boolean> {
  try {
    // This central public probe must not validate a tenant bearer token
    // against the central Passport database or trigger auth/retry toasts.
    await axios.get(`${(api.defaults.baseURL || '/api').replace(/\/$/, '')}/health`, { timeout: PROBE_TIMEOUT_MS, withCredentials: false, headers: { Authorization: false, 'X-Tenant-ID': false } });
    connectivityStore.markNavigatorOnline(true);
    connectivityStore.markServerReachable('up');
    return true;
  } catch (error: any) {
    return applyProbeResult(error?.status ?? error?.response?.status);
  }
}

export async function probeServerHealth(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    connectivityStore.markNavigatorOnline(false);
    // Offline, the server is unreachable by definition: reconcile a stale 'up'
    // once. Guarded, because markServerReachable('down') force-notifies and the
    // 'down' subscription would otherwise re-arm the 5s retry probe forever
    // while every attempt bailed out here without ever updating reachability.
    if (connectivityStore.getState().serverReachable !== 'down') {
      connectivityStore.markServerReachable('down');
    }
    return false;
  }
  if (probePromise) {
    // Join the probe that is actually running instead of reading whatever the
    // store happened to hold a moment ago: concurrent callers must resolve
    // with ITS result, not a stale pre-flight value.
    return probePromise;
  }
  probePromise = runHealthProbe().finally(() => {
    probePromise = null;
  });
  return probePromise;
}

function scheduleRetryProbe(): void {
  if (!probeUsers || retryProbeTimer) return;
  retryProbeTimer = setTimeout(() => {
    retryProbeTimer = null;
    void probeServerHealth();
  }, RETRY_PROBE_DELAY_MS);
}

function ensureProbeLoop(): void {
  probeUsers += 1;
  if (probeUsers > 1) return;

  void probeServerHealth();
  probeTimer = setInterval(() => {
    // A hidden tab neither sells nor needs fresh state — skip the request.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void probeServerHealth();
  }, PROBE_INTERVAL_MS);

  const onVisible = () => {
    if (document.visibilityState === 'visible') void probeServerHealth();
  };
  const onNavigatorOnline = () => connectivityStore.markNavigatorOnline(true);
  const onNavigatorOffline = () => connectivityStore.markNavigatorOnline(false);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onNavigatorOnline);
  window.addEventListener('offline', onNavigatorOffline);

  // Failure observations from the RTK base query expedite the next probe.
  const unsubscribeProbe = connectivityStore.subscribe(() => {
    if (connectivityStore.getState().serverReachable === 'down') scheduleRetryProbe();
  });

  releaseProbeLoopCleanup = () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onNavigatorOnline);
    window.removeEventListener('offline', onNavigatorOffline);
    unsubscribeProbe();
  };
}

let releaseProbeLoopCleanup: () => void = () => {};

function releaseProbeLoop(): void {
  probeUsers = Math.max(0, probeUsers - 1);
  if (probeUsers > 0) return;

  if (probeTimer) {
    clearInterval(probeTimer);
    probeTimer = null;
  }
  if (retryProbeTimer) {
    clearTimeout(retryProbeTimer);
    retryProbeTimer = null;
  }
  releaseProbeLoopCleanup();
  releaseProbeLoopCleanup = () => {};
}

export function useConnectivity() {
  const state: ConnectivityState = useSyncExternalStore(
    connectivityStore.subscribe,
    connectivityStore.getState,
    connectivityStore.getState,
  );

  useEffect(() => {
    ensureProbeLoop();
    return releaseProbeLoop;
  }, []);

  const probeNow = useCallback(() => probeServerHealth(), []);

  // 'unknown' (cold start, first probe still in flight) is NOT online: a till
  // whose server has not been proven reachable must take the safe offline
  // (queue) sale branch — exactly the failure mode this model exists to catch.
  const isEffectivelyOnline = state.navigatorOnline && state.serverReachable === 'up';

  return {
    ...state,
    isEffectivelyOnline,
    probeNow,
  };
}

export default useConnectivity;
